import { useEffect } from "react";

// A5 (148mm x 210mm) double-sided printable handout for guests at the
// Metamorph private release shows. Front = full-bleed portrait keepsake.
// Back  = thank-you note from GM + QR code → vinyl / exclusive download.
//
// Print from the browser:  ⌘P / Ctrl+P  →  Paper: A5  →  Margins: None
// (Tested in Chrome / Safari. Page-break-after isolates each side so the
// printer outputs exactly 2 pages, ready for duplex print-and-cut.)

const PORTRAIT_IMG =
  "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/oivt9a3p__L2A0180.jpg";

const QR_IMG =
  "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/6yrab7bd_Untitled-2.jpg";

const NOTE = `Thank you for attending tonight to celebrate the private release of Metamorph.

I feel strongly about creating new improved pathways and systems for Artists to share their work, so in this sentiment, I felt I should explore a new way myself.

Metamorph is only available via direct download from a private website and vinyl until the end of July.

100% of everything is Artist written, produced and owned.

Thank you so much again.`;

export default function Handout() {
  useEffect(() => {
    document.title = "Metamorph — Handout (A5)";
  }, []);

  return (
    <div className="handout-root" data-testid="handout-page">
      {/* Screen-only toolbar */}
      <div className="handout-toolbar no-print">
        <div className="handout-toolbar-inner">
          <div className="handout-toolbar-text">
            <strong>Metamorph — A5 Handout</strong>
            <span>
              Print at <em>A5 · 100% scale · margins: none</em> · duplex (long edge)
            </span>
          </div>
          <button
            type="button"
            className="handout-print-btn"
            onClick={() => window.print()}
            data-testid="handout-print-btn"
          >
            Print
          </button>
        </div>
      </div>

      {/* FRONT — full-bleed portrait keepsake */}
      <section className="handout-page handout-front" data-testid="handout-front">
        <img
          src={PORTRAIT_IMG}
          alt="Metamorph — George Maple"
          className="handout-front-img"
        />
      </section>

      {/* BACK — note + QR */}
      <section className="handout-page handout-back" data-testid="handout-back">
        <div className="handout-back-inner">
          <header className="handout-back-header">
            <h1 className="handout-wordmark">Metamorph</h1>
            <p className="handout-byline">by George Maple</p>
          </header>

          <div className="handout-note">
            {NOTE.split("\n\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
            <p className="handout-sign-off">— GM x</p>
          </div>

          <footer className="handout-back-footer">
            <img src={QR_IMG} alt="Scan to access vinyl + exclusive download" className="handout-qr" />
            <div className="handout-qr-caption">
              <p className="handout-qr-line-1">Scan for vinyl + exclusive download</p>
              <p className="handout-qr-line-2">metamorph-george-maple.com</p>
            </div>
          </footer>
        </div>
      </section>

      {/* Inline styles — kept local so this page is fully self-contained. */}
      <style>{`
        @page { size: A5; margin: 0; }

        .handout-root {
          background: #1a1a1a;
          min-height: 100vh;
          padding: 32px 16px 64px;
          font-family: "Outfit", "Helvetica Neue", Helvetica, Arial, sans-serif;
          color: #1a1a1a;
        }

        .handout-toolbar {
          max-width: 148mm;
          margin: 0 auto 24px;
          background: #f3f2ee;
          border: 1px solid #c9c5b8;
          border-radius: 4px;
          padding: 14px 18px;
        }
        .handout-toolbar-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .handout-toolbar-text {
          display: flex;
          flex-direction: column;
          gap: 4px;
          font-size: 13px;
          line-height: 1.4;
          color: #2a2a2a;
        }
        .handout-toolbar-text em {
          font-style: normal;
          color: #6a6a6a;
        }
        .handout-print-btn {
          background: #1a1a1a;
          color: #f3f2ee;
          border: none;
          padding: 10px 24px;
          font-family: inherit;
          font-size: 11px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          cursor: pointer;
          border-radius: 2px;
        }
        .handout-print-btn:hover { background: #000; }

        /* A5 page — 148 × 210 mm */
        .handout-page {
          width: 148mm;
          height: 210mm;
          margin: 0 auto 24px;
          background: #f3f2ee;
          overflow: hidden;
          position: relative;
          box-shadow: 0 12px 40px rgba(0,0,0,0.45);
          page-break-after: always;
          break-after: page;
        }
        .handout-page:last-of-type {
          page-break-after: auto;
          break-after: auto;
          margin-bottom: 0;
        }

        /* FRONT */
        .handout-front { background: #0c0c0c; }
        .handout-front-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }

        /* BACK */
        .handout-back { background: #f3f2ee; }
        .handout-back-inner {
          width: 100%;
          height: 100%;
          padding: 14mm 13mm 11mm;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
        }
        .handout-back-header { text-align: center; margin-bottom: 8mm; }
        .handout-wordmark {
          font-family: "Cormorant Garamond", "Playfair Display", Georgia, serif;
          font-weight: 500;
          font-size: 32pt;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          margin: 0;
          line-height: 1;
          color: #1a1a1a;
        }
        .handout-byline {
          font-family: "JetBrains Mono", "Courier New", monospace;
          font-size: 7.5pt;
          letter-spacing: 0.4em;
          text-transform: uppercase;
          opacity: 0.7;
          margin: 6px 0 0;
        }

        .handout-note {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 3.4mm;
          font-family: "Cormorant Garamond", "Garamond", "Hoefler Text", Georgia, serif;
          font-weight: 400;
          font-size: 12.5pt;
          line-height: 1.55;
          letter-spacing: 0.005em;
          color: #2a2a2a;
          padding: 0 1mm;
        }
        .handout-note p { margin: 0; }
        .handout-sign-off {
          margin-top: 4mm !important;
          font-family: "Cormorant Garamond", Georgia, serif;
          font-style: italic;
          font-weight: 400;
          font-size: 13.5pt;
          text-align: right;
        }

        .handout-back-footer {
          display: flex;
          align-items: center;
          gap: 6mm;
          padding-top: 5mm;
          border-top: 1px solid rgba(26,26,26,0.18);
        }
        .handout-qr {
          width: 28mm;
          height: 28mm;
          object-fit: contain;
          flex-shrink: 0;
        }
        .handout-qr-caption {
          display: flex;
          flex-direction: column;
          gap: 2mm;
        }
        .handout-qr-line-1 {
          font-size: 9pt;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #1a1a1a;
          margin: 0;
        }
        .handout-qr-line-2 {
          font-size: 8pt;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          opacity: 0.55;
          margin: 0;
          font-family: "JetBrains Mono", "Courier New", monospace;
        }

        /* PRINT */
        @media print {
          html, body, #root { background: #fff !important; }
          .handout-root {
            background: #fff;
            padding: 0;
            margin: 0;
          }
          .no-print { display: none !important; }
          .handout-page {
            margin: 0 !important;
            box-shadow: none !important;
            width: 148mm;
            height: 210mm;
          }
        }
      `}</style>
    </div>
  );
}
